import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Card,
  Table,
  Button,
  Typography,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Drawer,
  Tag,
  message,
  Spin,
} from 'antd';
import { PlusOutlined, LinkOutlined, BellOutlined, LineChartOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../config/api';

const { Title, Text } = Typography;

interface ProblemRow {
  id: string;
  title: string;
  status: string;
  target_date?: string | null;
  owner_first_name?: string | null;
  owner_last_name?: string | null;
  linked_ticket_count?: number;
  root_cause?: string | null;
}

interface InsightRow {
  id: string;
  title?: string;
  body?: string;
  created_at: string;
  data?: {
    alert_kind?: string;
    business_name?: string;
    business_id?: string;
    signature_field?: string;
    signature_field_key?: string;
    category_id?: string;
    issue_value?: string;
    top_matches?: { ticket_number?: string; score?: number }[];
  };
}

const Problems: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [linkTicketOpen, setLinkTicketOpen] = useState(false);
  const [form] = Form.useForm();
  const [linkForm] = Form.useForm();

  const { data: listData, isLoading: listLoading } = useQuery(
    ['problems-list'],
    async () => (await api.get('/api/problems')).data
  );

  const { data: detailData, isLoading: detailLoading } = useQuery(
    ['problem-detail', detailId],
    async () => (await api.get(`/api/problems/${detailId}`)).data,
    { enabled: !!detailId }
  );

  const { data: assignable } = useQuery(
    ['users-assignable-problems'],
    async () => (await api.get('/api/users/assignable')).data
  );

  const { data: insightsData, isLoading: insightsLoading, refetch: refetchInsights } = useQuery(
    ['problem-insights'],
    async () => (await api.get('/api/problems/insights', { params: { limit: 50, days: 90 } })).data,
    { staleTime: 60000 }
  );

  const createMutation = useMutation(
    async (values: Record<string, unknown>) => {
      const payload = {
        ...values,
        target_date: values.target_date ? (values.target_date as dayjs.Dayjs).format('YYYY-MM-DD') : null,
      };
      await api.post('/api/problems', payload);
    },
    {
      onSuccess: () => {
        message.success('Problem created');
        queryClient.invalidateQueries(['problems-list']);
        queryClient.invalidateQueries(['problem-insights']);
        setCreateOpen(false);
        form.resetFields();
      },
      onError: () => {
        message.error('Failed to create problem');
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, values }: { id: string; values: Record<string, unknown> }) => {
      await api.patch(`/api/problems/${id}`, values);
    },
    {
      onSuccess: () => {
        message.success('Updated');
        queryClient.invalidateQueries(['problems-list']);
        queryClient.invalidateQueries(['problem-detail', detailId]);
      },
      onError: () => {
        message.error('Update failed');
      },
    }
  );

  const linkMutation = useMutation(
    async ({ problemId, ticketNumber }: { problemId: string; ticketNumber: string }) => {
      const t = await api.get(`/api/tickets/number/${encodeURIComponent(ticketNumber.trim())}`);
      const ticketId = t.data?.id;
      if (!ticketId) throw new Error('Ticket not found');
      await api.post(`/api/problems/${problemId}/tickets`, { ticket_id: ticketId });
    },
    {
      onSuccess: () => {
        message.success('Ticket linked');
        queryClient.invalidateQueries(['problem-detail', detailId]);
        queryClient.invalidateQueries(['problems-list']);
        setLinkTicketOpen(false);
        linkForm.resetFields();
      },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: string } } };
        message.error(err?.response?.data?.error || 'Could not link ticket');
      },
    }
  );

  const unlinkMutation = useMutation(
    async ({ problemId, ticketId }: { problemId: string; ticketId: string }) => {
      await api.delete(`/api/problems/${problemId}/tickets/${ticketId}`);
    },
    {
      onSuccess: () => {
        message.success('Unlinked');
        queryClient.invalidateQueries(['problem-detail', detailId]);
        queryClient.invalidateQueries(['problems-list']);
      },
    }
  );

  const problems: ProblemRow[] = listData?.problems || [];
  const insights = insightsData?.insights || [];

  const alertKindLabel = (kind: string | undefined) => {
    switch (kind) {
      case 'volume_7d':
        return 'Volume (7d)';
      case 'digest_signature':
        return 'Chronic pattern';
      case 'subject_similarity':
        return 'Similar subjects';
      default:
        return kind || 'Alert';
    }
  };

  const openCreateFromInsight = (row: InsightRow) => {
    const d = row.data || {};
    const businessName = (d.business_name as string) || 'Business';
    const lines = [row.body || '', ''];
    if (d.alert_kind === 'subject_similarity' && d.top_matches && Array.isArray(d.top_matches)) {
      lines.push(
        'Similar tickets: ' +
          d.top_matches.map((m) => `#${m.ticket_number} (${m.score})`).join(', ')
      );
    }
    form.setFieldsValue({
      title: `Recurring: ${businessName}`,
      description: lines.filter(Boolean).join('\n'),
      signature_field_key: (d.signature_field as string) || (d.signature_field_key as string) || undefined,
      category_id: (d.category_id as string) || undefined,
      issue_value: d.issue_value != null ? String(d.issue_value) : undefined,
    });
    setCreateOpen(true);
  };

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} align="center">
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Problems (root cause)
          </Title>
          <Text type="secondary">
            Track underlying causes for recurring incidents. Link related tickets from the drawer.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setCreateOpen(true);
          }}
        >
          New problem
        </Button>
      </Space>

      <Card
        style={{ marginBottom: 24 }}
        title={
          <Space>
            <BellOutlined />
            <span>System alerts (recurring issues)</span>
          </Space>
        }
        extra={
          <Button size="small" onClick={() => refetchInsights()}>
            Refresh
          </Button>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Automated signals from Analytics (volume, chronic patterns, similar subjects). Same items as your in-app
          notifications — use them to open a problem record or jump to Analytics.
        </Text>
        {insightsLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : insights.length === 0 ? (
          <Text type="secondary">No alerts yet. They appear when thresholds are met (see server env / Analytics).</Text>
        ) : (
          <Table
            size="small"
            rowKey="id"
            dataSource={insights as InsightRow[]}
            pagination={{ pageSize: 8 }}
            columns={[
              {
                title: 'When',
                dataIndex: 'created_at',
                key: 'created_at',
                width: 160,
                render: (d: string) => dayjs(d).format('MMM D, YYYY HH:mm'),
              },
              {
                title: 'Type',
                key: 'kind',
                width: 130,
                render: (_: unknown, row: InsightRow) => (
                  <Tag color="orange">{alertKindLabel(row.data?.alert_kind)}</Tag>
                ),
              },
              {
                title: 'Summary',
                key: 'summary',
                ellipsis: true,
                render: (_: unknown, row: InsightRow) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{row.title}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {(row.body || '').slice(0, 120)}
                      {(row.body || '').length > 120 ? '…' : ''}
                    </Text>
                  </div>
                ),
              },
              {
                title: 'Business',
                key: 'biz',
                width: 180,
                ellipsis: true,
                render: (_: unknown, row: InsightRow) => row.data?.business_name || '—',
              },
              {
                title: '',
                key: 'actions',
                width: 220,
                render: (_: unknown, row: InsightRow) => (
                  <Space size="small" wrap>
                    <Button type="link" size="small" onClick={() => openCreateFromInsight(row)}>
                      Create problem
                    </Button>
                    <Button
                      type="link"
                      size="small"
                      icon={<LineChartOutlined />}
                      onClick={() => navigate('/analytics')}
                    >
                      Analytics
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Card>
        {listLoading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            size="small"
            rowKey="id"
            dataSource={problems}
            onRow={(record) => ({
              onClick: () => setDetailId(record.id),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: 'Title', dataIndex: 'title', key: 'title', ellipsis: true },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 120,
                render: (s: string) => {
                  const c = s === 'resolved' ? 'green' : s === 'in_progress' ? 'blue' : 'default';
                  return <Tag color={c}>{s.replace('_', ' ')}</Tag>;
                },
              },
              {
                title: 'Owner',
                key: 'owner',
                width: 160,
                render: (_: unknown, r: ProblemRow) =>
                  r.owner_first_name || r.owner_last_name
                    ? `${r.owner_first_name || ''} ${r.owner_last_name || ''}`.trim()
                    : '—',
              },
              {
                title: 'Target',
                dataIndex: 'target_date',
                key: 'target_date',
                width: 120,
                render: (d: string | null) => (d ? dayjs(d).format('YYYY-MM-DD') : '—'),
              },
              {
                title: 'Tickets',
                dataIndex: 'linked_ticket_count',
                key: 'linked_ticket_count',
                width: 80,
              },
            ]}
            pagination={{ pageSize: 15 }}
          />
        )}
      </Card>

      <Modal
        title="New problem"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={createMutation.isLoading}
        width={560}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => createMutation.mutate(values)}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="Short title for the underlying issue" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Context, scope, affected businesses" />
          </Form.Item>
          <Form.Item name="root_cause" label="Root cause (when known)">
            <Input.TextArea rows={2} placeholder="Permanent fix or vendor dependency" />
          </Form.Item>
          <Form.Item name="owner_user_id" label="Owner">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Assign owner"
              options={(assignable || []).map((u: { id: string; first_name?: string; last_name?: string }) => ({
                value: u.id,
                label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="open">
            <Select
              options={[
                { value: 'open', label: 'Open' },
                { value: 'in_progress', label: 'In progress' },
                { value: 'resolved', label: 'Resolved' },
              ]}
            />
          </Form.Item>
          <Form.Item name="target_date" label="Target date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="signature_field_key" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="category_id" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="issue_value" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={detailData?.problem?.title || 'Problem'}
        width={720}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        extra={
          <Space>
            <Button
              icon={<LinkOutlined />}
              onClick={() => {
                setLinkTicketOpen(true);
              }}
            >
              Link ticket
            </Button>
          </Space>
        }
      >
        {detailLoading || !detailData ? (
          <Spin />
        ) : (
          <>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text type="secondary">Status</Text>
                <div>
                  <Select
                    style={{ minWidth: 160 }}
                    value={detailData.problem.status}
                    onChange={(status) =>
                      updateMutation.mutate({ id: detailData.problem.id, values: { status } })
                    }
                    options={[
                      { value: 'open', label: 'Open' },
                      { value: 'in_progress', label: 'In progress' },
                      { value: 'resolved', label: 'Resolved' },
                    ]}
                  />
                </div>
              </div>
              <div>
                <Text type="secondary">Description</Text>
                <Input.TextArea
                  rows={3}
                  defaultValue={detailData.problem.description || ''}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v !== (detailData.problem.description || '')) {
                      updateMutation.mutate({ id: detailData.problem.id, values: { description: v } });
                    }
                  }}
                />
              </div>
              <div>
                <Text type="secondary">Root cause</Text>
                <Input.TextArea
                  rows={2}
                  defaultValue={detailData.problem.root_cause || ''}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v !== (detailData.problem.root_cause || '')) {
                      updateMutation.mutate({ id: detailData.problem.id, values: { root_cause: v } });
                    }
                  }}
                />
              </div>
            </Space>

            <Title level={5} style={{ marginTop: 24 }}>
              Linked tickets
            </Title>
            <Table
              size="small"
              rowKey="id"
              dataSource={detailData.tickets || []}
              columns={[
                {
                  title: 'Ticket',
                  dataIndex: 'ticket_number',
                  key: 'ticket_number',
                  render: (n: string) => (
                    <Text
                      code
                      style={{ cursor: 'pointer', color: '#1890ff' }}
                      onClick={() => navigate(`/workspace/number/${n}`)}
                    >
                      {n}
                    </Text>
                  ),
                },
                { title: 'Subject', dataIndex: 'subject', key: 'subject', ellipsis: true },
                {
                  title: '',
                  key: 'x',
                  width: 80,
                  render: (_: unknown, row: { id: string }) => (
                    <Button
                      type="link"
                      danger
                      size="small"
                      onClick={() =>
                        unlinkMutation.mutate({ problemId: detailData.problem.id, ticketId: row.id })
                      }
                    >
                      Remove
                    </Button>
                  ),
                },
              ]}
              pagination={false}
            />
          </>
        )}
      </Drawer>

      <Modal
        title="Link ticket by number"
        open={linkTicketOpen}
        onCancel={() => {
          setLinkTicketOpen(false);
          linkForm.resetFields();
        }}
        onOk={() => linkForm.submit()}
        confirmLoading={linkMutation.isLoading}
      >
        <Form
          form={linkForm}
          layout="vertical"
          onFinish={(values) => {
            if (detailId && values.ticket_number) {
              linkMutation.mutate({ problemId: detailId, ticketNumber: values.ticket_number });
            }
          }}
        >
          <Form.Item
            name="ticket_number"
            label="Ticket #"
            rules={[{ required: true, message: 'Enter ticket number' }]}
          >
            <Input placeholder="e.g. 12345" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Problems;
